import React, { useMemo } from 'react';
import { Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { QueryResponse, EmployeeRow } from '../../../services/pw9339Api';

const { Text } = Typography;

interface ResultTableProps {
  result: QueryResponse;
}

const ResultTable: React.FC<ResultTableProps> = ({ result }) => {
  // Build dynamic columns based on salary items
  const columns: ColumnsType<EmployeeRow & { _key: string }> = useMemo(() => {
    const baseCols: ColumnsType<EmployeeRow & { _key: string }> = [
      {
        title: '部門',
        dataIndex: 'deptCode',
        key: 'deptCode',
        width: 80,
        fixed: 'left',
        render: (code: string, record) =>
          `${(code || '').trim()} ${(record.deptName || '').trim()}`,
      },
      {
        title: '人員',
        dataIndex: 'empCode',
        key: 'empCode',
        width: 120,
        fixed: 'left',
        render: (code: string, record) =>
          `${(code || '').trim()} ${(record.empName || '').trim()}`,
      },
    ];

    // Dynamic salary item columns
    const itemCols: ColumnsType<EmployeeRow & { _key: string }> = (result.columns || []).map(
      (col, idx) => ({
        title: (
          <span style={{ fontSize: 11 }}>
            {col.isSpecial ? <Text strong>{col.name}</Text> : col.name}
          </span>
        ),
        key: `item_${idx}`,
        width: 90,
        align: 'right' as const,
        render: (_: unknown, record: EmployeeRow & { _key: string }) => {
          const val = record.items?.[idx] ?? 0;
          if (col.code === '2SUM') {
            return <Tag color="blue">{val.toLocaleString()}</Tag>;
          }
          return val === 0 ? '-' : val.toLocaleString();
        },
      })
    );

    return [...baseCols, ...itemCols];
  }, [result.columns]);

  // Flatten all employees from all dept groups
  const dataSource = useMemo(() => {
    const rows: (EmployeeRow & { _key: string })[] = [];
    (result.deptGroups || []).forEach((grp, gi) => {
      (grp.employees || []).forEach((emp, ei) => {
        rows.push({
          ...emp,
          _key: `${gi}-${emp.empCode}-${ei}`,
        });
      });
    });
    return rows;
  }, [result.deptGroups]);

  // Summary row
  const summaryRow = useMemo(() => {
    if (!result.grandTotals || result.grandTotals.length === 0) return null;
    return result.grandTotals;
  }, [result.grandTotals]);

  return (
    <div>
      <div className="mb-2 text-sm text-gray-600">
        年月: {result.yearMonth} | 部門: {result.deptGroups?.length || 0} 個 | 員工: {result.totalEmps} 人
      </div>
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="_key"
        size="small"
        scroll={{ x: 200 + (result.columns?.length || 0) * 90, y: 500 }}
        pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200'] }}
        summary={() =>
          summaryRow ? (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <Text strong>合計</Text>
                </Table.Summary.Cell>
                {summaryRow.map((val, idx) => (
                  <Table.Summary.Cell key={idx} index={idx + 2} align="right">
                    <Text strong>{val === 0 ? '-' : val.toLocaleString()}</Text>
                  </Table.Summary.Cell>
                ))}
              </Table.Summary.Row>
            </Table.Summary>
          ) : null
        }
      />
    </div>
  );
};

export default ResultTable;
